/** @jsxImportSource @emotion/react */
import { useCallback, useMemo, useState } from 'react'
import { Button, Card, Form, Spinner } from 'react-bootstrap'
import moment from 'moment'
import Select from 'react-select'

import DataTable from 'components/atoms/DataTable'
import { TableWrapper } from 'components/atoms/TableWrapper'
import {
  useExportPreferenceSetupReportMutation,
  useGetPreferenceSetupReportQuery,
} from 'store/services/managementReports'
import {
  PreferenceSetupEntityType,
} from 'ts-back-end/src/reporting/types/preference-setup-report'

type Option<T extends string> = {
  label: string
  value: T
}

type AppliedFilters = {
  entityType?: PreferenceSetupEntityType
  search?: string
}

const PAGE_SIZE_DEFAULT = 20

const entityTypeLabelMap: Record<string, string> = {
  OFFICE: 'Office',
  PHYSICIAN_GROUP: 'Physician Group',
  PHYSICIAN: 'Physician',
}

const entityTypeOptions: Option<PreferenceSetupEntityType>[] = [
  { label: 'Office', value: 'OFFICE' },
  { label: 'Physician Group', value: 'PHYSICIAN_GROUP' },
  { label: 'Physician', value: 'PHYSICIAN' },
]

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export const PreferenceSetupReport = () => {
  const [selectedEntityType, setSelectedEntityType] =
    useState<Option<PreferenceSetupEntityType> | null>(null)
  const [search, setSearch] = useState('')
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({})
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT)

  const { data, isFetching } = useGetPreferenceSetupReportQuery({
    page: currentPage,
    size: pageSize,
    ...appliedFilters,
  })
  const [exportPreferenceSetupReport, { isLoading: isExporting }] =
    useExportPreferenceSetupReportMutation()

  const handleSearch = useCallback(() => {
    setCurrentPage(0)
    setAppliedFilters({
      entityType: selectedEntityType?.value,
      search: search.trim() || undefined,
    })
  }, [search, selectedEntityType])

  const handleClear = useCallback(() => {
    setSelectedEntityType(null)
    setSearch('')
    setCurrentPage(0)
    setAppliedFilters({})
  }, [])

  const handleExport = useCallback(async () => {
    const blob = await exportPreferenceSetupReport(appliedFilters).unwrap()
    downloadBlob(blob, `preference-setup-report-${moment().format('YYYY-MM-DD')}.csv`)
  }, [appliedFilters, exportPreferenceSetupReport])

  const tableColumns = useMemo(
    () => [
      {
        Header: 'Level',
        accessor: 'entityType',
        className: 'text-nowrap',
        Cell: ({ value }: { value: string }) => entityTypeLabelMap[value] || value,
      },
      { Header: 'Name', accessor: 'entityName' },
      {
        Header: 'Client',
        accessor: 'parentName',
        Cell: ({ value }: { value?: string | null }) => value || '—',
      },
      {
        Header: 'NPI',
        accessor: 'npi',
        className: 'text-nowrap',
        Cell: ({ value }: { value?: string | null }) => value || '—',
      },
      {
        Header: 'Preferred Device Types',
        accessor: 'preferredDeviceTypes',
        Cell: ({ value }: { value?: string[] | null }) => value?.join(', ') || '—',
      },
      {
        Header: 'Follow-up Care Providers',
        accessor: 'followUpProviders',
        Cell: ({ row }: { row: any }) =>
          row.original.isFollowUpCareNone ? 'None' : row.original.followUpProviders || '—',
      },
      {
        Header: 'Preferred DME Office',
        accessor: 'preferredDmeOfficeName',
        Cell: ({ row }: { row: any }) =>
          row.original.isPreferredDmeNone ? 'None' : row.original.preferredDmeOfficeName || '—',
      },
    ],
    [],
  )

  const tableData = useMemo(
    () => ({
      items: data?.entities?.items || [],
      currentPage: data?.entities?.currentPage ?? currentPage,
      totalItems: data?.entities?.totalItems ?? 0,
    }),
    [currentPage, data],
  )

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <Card className="mt-3">
            <Card.Header>
              <div className="d-flex flex-wrap align-items-center justify-content-between">
                <h4 className="mb-0">Follow-up Care Preference Setup</h4>
                {(isFetching || isExporting) && <Spinner animation="border" size="sm" />}
              </div>
            </Card.Header>
            <Card.Body>
              <div className="row mb-3">
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Level</label>
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    value={selectedEntityType}
                    onChange={(option) =>
                      setSelectedEntityType(option as Option<PreferenceSetupEntityType> | null)
                    }
                    options={entityTypeOptions}
                  />
                </div>
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Name or NPI</label>
                  <Form.Control
                    type="text"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => event.key === 'Enter' && handleSearch()}
                  />
                </div>
                <div className="col-md-6 col-sm-12 mb-2 d-flex align-items-end">
                  <Button variant="primary" onClick={handleSearch} disabled={isFetching}>
                    Search
                  </Button>
                  <Button variant="outline-secondary" className="ml-2" onClick={handleClear}>
                    Clear
                  </Button>
                  <Button
                    variant="outline-success"
                    className="ml-2"
                    onClick={handleExport}
                    disabled={isExporting || !tableData.totalItems}>
                    Export CSV
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <Card className="mt-3">
            <Card.Header>
              <div className="d-flex flex-wrap align-items-center justify-content-between">
                <h4 className="mb-0">Configured Preferences</h4>
                {isFetching && <Spinner animation="border" size="sm" />}
              </div>
            </Card.Header>
            <Card.Body>
              <TableWrapper
                className="card-body row"
                css={{
                  '& thead': {
                    zIndex: '0 !important',
                  },
                }}>
                <DataTable
                  tableKey="preference_setup_report"
                  data={tableData}
                  status={isFetching}
                  tableColumns={tableColumns}
                  pageSize={pageSize}
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                  setPageSize={setPageSize}
                  setSortOptions={() => {}}
                  searchable={false}
                  entityName="entities"
                />
              </TableWrapper>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default PreferenceSetupReport
